'use client';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { AUTH_CONFIG } from '@eliadmin/core/src/constants/auth-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { DialogClose, DialogTitle } from '@eliadmin/core/src/components/ui/dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuEyeOff } from 'react-icons/lu';
import { FaEye } from 'react-icons/fa';
export const signInSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': __eliadmin_zod_1().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
};

export function SignInForm({ onSuccess, initialSocialAuthProviders, inDialog }: SignInFormProps) {
  const { t } = useTranslations();
  const Title = inDialog ? DialogTitle : 'h3';

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      const resp = await signIn({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text656'),
        variant: 'success',
      });
      if (onSuccess) {
        onSuccess();
        return;
      }
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col space-y-4', 'ea-sign-in-form')}
        onSubmit={handleSubmit}
      >
        {/* header */}
        <div className={cn('mt-2 px-12 py-2', 'ea-sign-in-form__header')}>
          <Title
            className={cn(
              'mb-4 text-center text-3xl font-normal sm:text-4xl',
              'ea-sign-in-form__header-title',
            )}
          >
            {t('text190')}
          </Title>
          <p
            className={cn('text-center text-sm text-foreground', ' ea-sign-in-form__header-prompt')}
          >
            {t('text2292')}{' '}
            {inDialog ? (
              <DialogClose asChild>
                <a
                  href={ROUTES.REGISTER() ?? '#'}
                  className={cn(
                    'font-medium text-primary underline',
                    'ea-sign-in-form__header-link',
                  )}
                >
                  vytvorte si ho tu.
                </a>
              </DialogClose>
            ) : (
              <Link
                href={ROUTES.REGISTER() ?? '#'}
                className={cn('font-medium text-primary underline', 'ea-sign-in-form__header-link')}
                prefetch={false}
              >
                vytvorte si ho tu.
              </Link>
            )}
          </p>
        </div>

        {/* content */}
        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div className={cn(' flex flex-col space-y-4', 'ea-sign-in-form__content')}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-sign-in-form__email-field')}>
                <FormLabel className={cn('', 'ea-sign-in-form__email-label')}>
                  {t('text35')}
                </FormLabel>
                <FormControl className={cn('', 'ea-sign-in-form__email-input')}>
                  <Input
                    {...field}
                    placeholder={t('text259')}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                  />
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__email-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-sign-in-form__password-field')}>
                <FormLabel className={cn('', 'ea-sign-in-form__password-label')}>
                  {t('text191')}
                </FormLabel>
                <FormControl className={cn('', 'ea-sign-in-form__password-input')}>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                      type={showPassword ? 'text' : 'password'}
                      after={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onMouseDown={handlePasswordMouseDown}
                          onMouseUp={handlePasswordMouseUp}
                          onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                          onTouchStart={handlePasswordMouseDown}
                          onTouchEnd={handlePasswordMouseUp}
                        >
                          {showPassword ? (
                            <LuEyeOff size={20} className="text-[#838383]" />
                          ) : (
                            <FaEye size={20} className="text-[#838383]" />
                          )}
                        </Button>
                      }
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__password-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* FOOTER */}
        <div className={cn('flex flex-col items-start space-y-4 pb-12', 'ea-sign-in-form__footer')}>
          {/* <div
              className={cn(
                'flex flex-row items-center space-x-2',
                'ea-sign-in-form__checkbox-wrapper',
              )}
            >
              <Checkbox id="remember-me" className={cn('', 'ea-sign-in-form__checkbox')} />
              <Label htmlFor="remember-me" className={cn('', 'ea-sign-in-form__checkbox-label')}>
                {t('text260')}
              </Label>
            </div> */}
          <div className="flex w-full items-center justify-center">
            <Button
              type="submit"
              className={cn('w-full', 'ea-sign-in-form__submit-button')}
              isLoading={form.formState.isSubmitting}
            >
              {t('text189')}
            </Button>
          </div>

          {AUTH_CONFIG.signIn.passwordResetPosition === 'inside' && (
            <div className={cn('mx-auto flex justify-center', 'ea_sign-in-form__password-reset')}>
              <Link
                className={cn(
                  'w-full max-w-[400px] text-left text-sm font-medium underline',
                  'ea_sign-in-form__password-reset-link ',
                )}
                href={ROUTES.PASSWORD_RESET() ?? '#'}
                prefetch={false}
              >
                {t('text188')}
              </Link>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
}
